import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import styled from 'styled-components';
import ConsoleInterface from '../components/ConsoleInterface';
import { useAuth } from '../contexts/AuthContext';
import { getUserAssets, normalizeAsset } from '../services/assetService';
import { getUserTransactions, Transaction } from '../services/transactionService';
import { getWalletBalance } from '../services/walletService';
import DashboardLayout from '../components/DashboardLayout';

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const InfoBox = styled.div`
  background-color: rgba(0, 192, 239, 0.1);
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 5px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
  text-align: center;
  box-shadow: 0 0 10px rgba(0, 192, 239, 0.3);
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.9rem;
  }
`;

const InfoLink = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: underline;
  font-weight: bold;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.accent};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const WelcomeText = styled.h2`
  color: ${props => props.theme.colors.text};
  font-size: 1.2rem;
  
  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
  }
`;

const WalletInfo = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: #0a0a0a;
  border-radius: 4px;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    padding: 0.75rem;
  }
`;

const WalletBalance = styled.div`
  margin-right: 1.5rem;
  
  span {
    color: ${props => props.theme.colors.primary};
    font-weight: bold;
  }
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 0.5rem;
    width: 100%;
  }
`;

const WalletAddress = styled.div`
  display: flex;
  align-items: center;
  
  span {
    opacity: 0.7;
    margin-right: 0.5rem;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CopyButton = styled.button`
  background-color: transparent;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  
  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const RefreshButton = styled.button`
  background-color: transparent;
  border: 1px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(0, 192, 239, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AssetsSection = styled.div`
  margin-top: 1rem;
  background-color: #0a0a0a;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: 1rem;
  margin-bottom: 0;
  padding-bottom: 0.5rem;
`;

const AssetsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const AssetCard = styled.div`
  background-color: #161616;
  border-radius: 4px;
  padding: 0.8rem;
  border: 1px solid #333;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const AssetName = styled.div`
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const AssetAmount = styled.div`
  font-size: 1rem;
  margin-bottom: 0.2rem;
`;

const AssetValue = styled.div`
  font-size: 0.8rem;
  opacity: 0.7;
`;

const ActivitySection = styled.div`
  background-color: #0a0a0a;
  border-radius: 4px;
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActivityItem = styled.div`
  padding: 0.8rem;
  border-bottom: 1px solid #222;
  font-size: 0.9rem;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem;
    font-size: 0.85rem;
  }
`;

const ActivityDate = styled.span`
  color: ${props => props.theme.colors.primary};
  margin-right: 0.5rem;
`;

const TransactionHash = styled.span`
  font-family: monospace;
  background-color: #1a1a1a;
  padding: 0.2rem 0.4rem;
  border-radius: 2px;
  font-size: 0.8rem;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-size: 1.2rem;
`;

const ErrorBanner = styled.div`
  background-color: rgba(255, 85, 85, 0.2);
  color: ${props => props.theme.colors.error};
  padding: 0.5rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  text-align: center;
`;

const ShortenedAddress = styled.span`
  font-family: monospace;
  background-color: #1a1a1a;
  padding: 0.2rem 0.4rem;
  border-radius: 2px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #2a2a2a;
  }
`;

const CopyNotification = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #111;
  color: ${props => props.theme.colors.primary};
  padding: 10px 15px;
  border-radius: 4px;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
`;

const NavButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? '#000' : props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.primary};
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: ${props => !props.$active ? '10px' : '0'};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary : 'rgba(0, 192, 239, 0.1)'};
  }
`;

const TransactionDetails = styled.div`
  margin-top: 0.5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  font-size: 0.8rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TransactionDetail = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.2rem 0;
  border-bottom: 1px dotted #333;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  color: #999;
`;

const DetailValue = styled.span`
  font-weight: bold;
  color: ${props => props.theme.colors.text};
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 2px;
  font-size: 0.7rem;
  font-weight: bold;
  margin-left: 0.5rem;
  background-color: ${props => 
    props.status === 'completed' || props.status === 'success' ? '#133d13' : 
    props.status === 'pending' ? '#3d3913' : '#3d1313'};
  color: ${props => 
    props.status === 'completed' || props.status === 'success' ? '#5cff5c' : 
    props.status === 'pending' ? '#ffdc5c' : '#ff5c5c'};
`;

const TransactionLink = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-size: 0.8rem;
  display: inline-block;
  margin-top: 0.3rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const DebugAssetInfo = ({ assets }: { assets: Asset[] }) => {
  return null; // Empty fragment to disable debugging
};

const getTokenInfo = (token: string, assetsList: Asset[]): { symbol: string, isAddress: boolean } => {
  if (!token) return { symbol: 'UNKNOWN', isAddress: false };
  
  // First try to find an exact match in the assets
  const matchingAsset = assetsList.find(asset => 
    asset.token.toLowerCase() === token.toLowerCase()
  );
  
  if (matchingAsset && matchingAsset.symbol) {
    return { symbol: matchingAsset.symbol, isAddress: false };
  }
  
  // If no symbol found but it looks like a token address
  if (token.length > 20) {
    return { symbol: token, isAddress: true };
  }
  
  return { symbol: token, isAddress: false };
};

// Add new styled components for the How it Works modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(3px);
`;

const ModalContent = styled.div`
  background-color: #111;
  border-radius: 6px;
  border: 1px solid ${props => props.theme.colors.primary};
  box-shadow: 0 0 20px rgba(0, 192, 239, 0.3);
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: 3px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text};
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 10;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const HowItWorksContainer = styled.div`
  background-color: transparent;
  padding: 1.5rem;
`;

const HowItWorksTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const HowItWorksSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionHeading = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: 1.2rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.primary};
  padding-bottom: 0.5rem;
`;

const BulletList = styled.ul`
  list-style-type: disc;
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
`;

const CommandExample = styled.div`
  background-color: #161616;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1rem;
  border-left: 3px solid ${props => props.theme.colors.primary};
`;

const CommandTitle = styled.div`
  font-weight: bold;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const CommandSyntax = styled.div`
  font-family: monospace;
  background-color: #0c0c0c;
  padding: 0.5rem;
  border-radius: 3px;
  margin-bottom: 0.5rem;
`;

const CommandDescription = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
  line-height: 1.4;
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const NavButtonsGroup = styled.div`
  display: flex;
`;

const AddressGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    margin-left: auto;
  }
`;

const AddressChip = styled.div`
  display: flex;
  align-items: center;
  background-color: #161616;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 0.3rem 0.5rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: #1a1a1a;
  }
  
  svg {
    margin-right: 5px;
  }
`;

const AddressLabel = styled.span`
  color: ${props => props.theme.colors.primary};
  margin-right: 0.3rem;
  cursor: pointer;
`;

const AddressText = styled.span`
  font-family: monospace;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

// Add blockchain explorer icons
const SolscanIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.243 18.788L6.55 15.54l4.207-2.436 4.208 2.436-4.208 3.248zm.001-5.248L6.55 10.292l4.208-7.292 4.208 7.292-4.208 3.248z" />
  </svg>
);

const DexscreenerIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-2 16h4v2h-4v-2zm6-3H8v-2h8v2zm0-3H8V8h8v2z" />
  </svg>
);

// This is the only Asset interface declaration in the file
interface Asset {
  _id: string;
  token: string;
  symbol: string;
  amount: number;
  lastKnownPriceUsd: number;
}

// Add this utility function for shortening addresses
const shortenAddress = (address: string) => {
  if (!address || address === "Not available") return address;
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
};

const Console: React.FC = () => {
  const { logout: privyLogout, login: privyLogin } = usePrivy();
  const { user, logout: authLogout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('dashboard'); // Set dashboard as default
  const [assets, setAssets] = useState<Asset[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [walletBalance, setWalletBalance] = useState<number>(0); // Default to 0 instead of null
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [copyNotification, setCopyNotification] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [isCopying, setIsCopying] = useState(false); // Add this state for the "Copy" button
  const [isRefreshing, setIsRefreshing] = useState(false); // Add this state for the "Refresh" button
  const tokenAddress = "H6dZStmJBb5c6YAsnV5JxTbgwJiLjT5zKc4GeBwcpump";
  const pairAddress = "GjkTe6az5xLXpe4NUAtEc1fLxoLkXDH62nUZBxHZtCoC";

  // Update copyToClipboard function to use the isCopying state
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setIsCopying(true);
    setCopyNotification(`Copied: ${text.substring(0, 15)}...`);
    
    // Clear notification and reset copying state after 3 seconds
    setTimeout(() => {
      setCopyNotification(null);
      setIsCopying(false);
    }, 3000);
  };

  // Update refreshWalletBalance function to use the isRefreshing state
  const refreshWalletBalance = async () => {
    if (!user?.embeddedWalletAddress) return;
    
    try {
      setIsRefreshing(true);
      setLoadingBalance(true);
      const balanceData = await getWalletBalance(user.embeddedWalletAddress);
      
      if (balanceData) {
        setWalletBalance(balanceData.balance);
      }
    } catch (err) {
      console.error("Error fetching wallet balance:", err);
    } finally {
      setLoadingBalance(false);
      setIsRefreshing(false);
    }
  };

  const fetchAssets = async () => {
    if (!user?.privyUserId) return;
    
    try {
      setLoadingAssets(true);
      const assetsData = await getUserAssets(user.privyUserId);
      setAssets(assetsData || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoadingAssets(false);
    }
  };

  const fetchActivity = async () => {
    if (!user?.privyUserId) return;
    
    try {
      setLoadingActivity(true);
      const transactionsData = await getUserTransactions(user.privyUserId);
      setTransactions(transactionsData || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  useEffect(() => {
    if (user?.privyUserId) {
      setIsLoading(true);
      setError(null);
      
      Promise.all([
        fetchAssets(),
        fetchActivity()
      ])
      .catch(err => {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      })
      .finally(() => {
        setIsLoading(false);
      });

      // Add a safety timeout to ensure loading state is cleared even if requests hang
      const safetyTimeout = setTimeout(() => {
        setIsLoading(false);
      }, 10000); // 10 seconds timeout
      
      return () => clearTimeout(safetyTimeout);
    } else {
      // If no user is authenticated, ensure loading is set to false
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.embeddedWalletAddress) {
      refreshWalletBalance();
    }
  }, [user?.embeddedWalletAddress]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Add this effect to handle navigation when authentication is still loading
  useEffect(() => {
    // Set a timeout to ensure the app doesn't get stuck on loading
    const loadingTimeout = setTimeout(() => {
      if (isLoading) {
        console.warn('Loading state persisted too long - forcing reset');
        setIsLoading(false);
      }
    }, 15000); // 15 seconds max loading time
    
    return () => clearTimeout(loadingTimeout);
  }, [isLoading]);

  const handleLogout = async () => {
    try {
      await privyLogout();
      authLogout();
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const walletAddress = user?.embeddedWalletAddress || user?.walletAddress || "Not available";

  // Add this function to render the How It Works modal
  const renderHowItWorksModal = () => {
    if (!showHowItWorks) return null;
    
    return (
      <ModalOverlay onClick={() => setShowHowItWorks(false)}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={() => setShowHowItWorks(false)}>×</CloseButton>
          
          <HowItWorksContainer>
            <HowItWorksTitle>How Voovo Works</HowItWorksTitle>
            
            <HowItWorksSection>
              <SectionHeading>Who we are?</SectionHeading>
              <BulletList>
                <li>We are a X Trading Bot Voovo.</li>
                <li>Voovo is accessible to all the 600 Million+ X account Monthly active users.</li>
                <li>Send/Receive Solana</li>
                <li>Swap Solana coins right from your http://X.com</li>
                <li>Simply tag our account to start using Voovo.</li>
                <li>Study Voovo.</li>
                <li>Revenue Share and profit share to holders and Bot users.</li>
              </BulletList>
            </HowItWorksSection>
            
            <HowItWorksSection>
              <SectionHeading>How to use X Bot?</SectionHeading>
              <p style={{ marginBottom: '1rem' }}>Connect your X account to our Console.</p>
              
              <CommandExample>
                <CommandTitle>To Send:</CommandTitle>
                <CommandSyntax>"Send 100 SOL to @nickname"</CommandSyntax>
                <CommandDescription>Specify token (e.g., SOL) and amount, plus the recipient's X.com @nickname</CommandDescription>
              </CommandExample>
              
              <CommandExample>
                <CommandTitle>To Buy:</CommandTitle>
                <CommandSyntax>"Buy me 100 $VOOVO"</CommandSyntax>
                <CommandDescription>Name the token (e.g., VOOVO) and quantity. The amount is deducted from your wallet, and tokens are purchased instantly.</CommandDescription>
              </CommandExample>
              
              <CommandExample>
                <CommandTitle>To Sell:</CommandTitle>
                <CommandSyntax>"Sell 100 $VOOVO"</CommandSyntax>
                <CommandDescription>Indicate the token and amount. Proceeds are credited to your wallet after an automatic sale.</CommandDescription>
              </CommandExample>
              
              <CommandExample>
                <CommandTitle>To Swap:</CommandTitle>
                <CommandSyntax>"Swap 100 SOL for VOOVO"</CommandSyntax>
                <CommandDescription>Specify tokens and amount to swap. Voovo's bot ensures efficient trades and no front-runs with Jito enabled.</CommandDescription>
              </CommandExample>
              
              <CommandExample>
                <CommandTitle>To Check Balance:</CommandTitle>
                <CommandSyntax>"Check my balance"</CommandSyntax>
                <CommandDescription>Instantly view your wallet's token holdings and their current value.</CommandDescription>
              </CommandExample>
            </HowItWorksSection>
          </HowItWorksContainer>
        </ModalContent>
      </ModalOverlay>
    );
  };

  // Add this function to render the Disclaimer modal
  const renderDisclaimerModal = () => {
    if (!showDisclaimer) return null;
    
    return (
      <ModalOverlay onClick={() => setShowDisclaimer(false)}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={() => setShowDisclaimer(false)}>×</CloseButton>
          
          <HowItWorksContainer>
            <HowItWorksTitle>Disclaimer</HowItWorksTitle>
            
            <HowItWorksSection>
              <p>
                Voovo Trading Bot is a software tool designed for informational and educational purposes only. 
                It is not intended to provide financial, investment, or trading advice, nor does it constitute 
                a recommendation, solicitation, or endorsement to buy, sell, or hold any financial instruments, 
                including stocks, cryptocurrencies, commodities, or other assets. The use of Voovo Trading Bot 
                does not guarantee profits, and trading in financial markets involves substantial risks, including 
                the potential for significant financial loss, up to and including the total loss of your invested capital.
              </p>
              <p>
                Past performance of the Voovo Trading Bot, or any trading strategies it may employ, is not indicative 
                of future results. Market conditions are inherently volatile and subject to rapid change, and no 
                representations or warranties, express or implied, are made regarding the accuracy, reliability, or 
                completeness of the information provided by the bot. Users are solely responsible for their trading 
                decisions and outcomes resulting from the use of this software.
              </p>
              <p>
                Before using Voovo Trading Bot, users are strongly encouraged to conduct their own independent 
                research, carefully assess their financial situation, risk tolerance, and investment objectives, 
                and consult with a licensed financial advisor or qualified professional. Voovo Trading Bot is not 
                affiliated with any financial institution, exchange, or regulatory body, and its use is subject to 
                all applicable laws, regulations, and jurisdictional requirements.
              </p>
              <p>
                To the fullest extent permitted by law, Voovo, its developers, affiliates, and representatives 
                disclaim any and all liability for any direct, indirect, incidental, consequential, or special 
                damages, including but not limited to financial losses, data loss, or business interruptions, 
                arising from or in connection with the use of or inability to use Voovo Trading Bot, even if 
                advised of the possibility of such damages. By using Voovo Trading Bot, you acknowledge and 
                accept these risks and agree to use the software at your own discretion and responsibility.
              </p>
            </HowItWorksSection>
          </HowItWorksContainer>
        </ModalContent>
      </ModalOverlay>
    );
  };

  // Add this function to render the Terms modal
  const renderTermsModal = () => {
    if (!showTerms) return null;
    
    return (
      <ModalOverlay onClick={() => setShowTerms(false)}>
        <ModalContent onClick={e => e.stopPropagation()} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
          <CloseButton onClick={() => setShowTerms(false)}>×</CloseButton>
          
          <HowItWorksContainer>
            <HowItWorksTitle>Terms and Conditions</HowItWorksTitle>
            <p style={{ fontStyle: 'italic', opacity: 0.7, marginBottom: '1.5rem' }}>Last Updated: May 15, 2025</p>
            
            <HowItWorksSection>
              <p>
                Welcome to Voovo Trading Bot ("Voovo Trading Bot," "we," "us," or "our"). These Terms and Conditions ("Terms") 
                govern your access to and use of the Voovo Trading Bot website (the "Website") and the Voovo Trading Bot console 
                software (the "Console") (collectively, the "Services"). By accessing or using the Services, you ("User," 
                "you," or "your") agree to be bound by these Terms. If you do not agree with these Terms, you must not use 
                the Services.
              </p>
              
              <h3 style={{ color: '#00c0ef', marginTop: '1.5rem', marginBottom: '1rem' }}>1. Acceptance of Terms</h3>
              <p>
                By accessing the Website or using the Console, you confirm that you are at least 18 years of age, have the 
                legal capacity to enter into this agreement, and agree to comply with these Terms. We reserve the right to 
                modify these Terms at any time, and such changes will be effective upon posting on the Website. Your continued 
                use of the Services after such changes constitutes acceptance of the updated Terms.
              </p>
              
              <h3 style={{ color: '#00c0ef', marginTop: '1.5rem', marginBottom: '1rem' }}>2. Use of Services</h3>
              <h4 style={{ color: '#00c0ef', marginBottom: '0.5rem' }}>2.1 License</h4>
              <p>
                Subject to these Terms, Voovo Trading Bot grants you a limited, non-exclusive, non-transferable, revocable 
                license to access and use the Website and Console for personal, non-commercial purposes. You may not 
                sublicense, distribute, or otherwise transfer this license.
              </p>
              
              <h4 style={{ color: '#00c0ef', marginBottom: '0.5rem' }}>2.2 Eligibility</h4>
              <p>You represent and warrant that:</p>
              <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>You are at least 18 years old.</li>
                <li>You will comply with all applicable laws, regulations, and rules in your jurisdiction while using the Services.</li>
                <li>You will not use the Services for any unlawful or unauthorized purpose.</li>
              </ul>
              
              <h4 style={{ color: '#00c0ef', marginBottom: '0.5rem' }}>2.3 Prohibited Activities</h4>
              <p>You agree not to:</p>
              <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Modify, reverse-engineer, decompile, or attempt to derive the source code of the Console or Website.</li>
                <li>Use the Services to engage in fraudulent, illegal, or harmful activities.</li>
                <li>Attempt to gain unauthorized access to the Services, servers, or networks.</li>
                <li>Use automated scripts, bots, or other means to scrape or extract data from the Website or Console.</li>
                <li>Interfere with or disrupt the functionality of the Services or other users' experience.</li>
              </ul>
              
              <h3 style={{ color: '#00c0ef', marginTop: '1.5rem', marginBottom: '1rem' }}>3. Risk Acknowledgment</h3>
              <p>
                The Voovo Trading Bot Console is provided for informational and educational purposes only and does not constitute 
                financial, investment, or trading advice. Trading in financial markets carries significant risks, including the 
                potential for substantial or total loss of invested capital. You acknowledge and agree that:
              </p>
              <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>You are solely responsible for your trading decisions and outcomes.</li>
                <li>Past performance of the Console is not indicative of future results.</li>
                <li>Voovo Trading Bot does not guarantee profits or the accuracy of any data or strategies provided.</li>
                <li>You should consult a qualified financial advisor before using the Console.</li>
              </ul>
              
              <h3 style={{ color: '#00c0ef', marginTop: '1.5rem', marginBottom: '1rem' }}>4. Account and Security</h3>
              <p>
                For full terms and conditions, please refer to our complete documentation.
              </p>
            </HowItWorksSection>
          </HowItWorksContainer>
        </ModalContent>
      </ModalOverlay>
    );
  };

  const renderContent = () => {
    if (activeSection === 'console') {
      return <ConsoleInterface onLogin={privyLogin} />;
    }

    if (activeSection === 'how-it-works') {
      return renderHowItWorksModal();
    }

    if (activeSection === 'disclaimer') {
      return renderDisclaimerModal();
    }

    if (activeSection === 'terms') {
      return renderTermsModal();
    }

    // Dashboard section
    return (
      <>
        <InfoBox>
          VOOVO Launches Holders Reward System, Investment possibility to earn up to 7% daily reward by investing into our utility: <InfoLink href="https://earn.voovo.app/" target="_blank" rel="noopener noreferrer">https://earn.voovo.app</InfoLink>
        </InfoBox>

        <Header>
          <WelcomeText>Welcome, {user?.email || 'Trader'}</WelcomeText>
          <WalletInfo>
            <WalletBalance>
              Balance: <span>{typeof walletBalance === 'number' ? walletBalance.toFixed(4) : '0.0000'} SOL</span>
            </WalletBalance>
            <WalletAddress>
              <span>{shortenAddress(walletAddress)}</span>
              <CopyButton onClick={() => copyToClipboard(walletAddress)} disabled={isCopying}>
                {isCopying ? 'Copied!' : 'Copy'}
              </CopyButton>
            </WalletAddress>
          </WalletInfo>
        </Header>
        
        {/* Render assets section */}
        <AssetsSection>
          <SectionHeader>
            <SectionTitle>My Assets</SectionTitle>
            <RefreshButton onClick={refreshWalletBalance} disabled={isRefreshing}>
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </RefreshButton>
          </SectionHeader>
          {assets.length === 0 ? (
            <div style={{ padding: '1rem 0', opacity: 0.7 }}>No assets found. Send funds to your wallet to get started.</div>
          ) : (
            <AssetsList>
              {assets.map(asset => (
                <AssetCard key={asset._id || asset.token}>
                  <AssetName>{asset.symbol || asset.token}</AssetName>
                  <AssetAmount>{asset.amount.toFixed(asset.symbol === 'SOL' ? 4 : 2)}</AssetAmount>
                  {asset.lastKnownPriceUsd > 0 && (
                    <AssetValue>${(asset.lastKnownPriceUsd * asset.amount).toFixed(2)} USD</AssetValue>
                  )}
                </AssetCard>
              ))}
            </AssetsList>
          )}
        </AssetsSection>
        
        {/* Render activity section */}
        <ActivitySection>
          <SectionHeader>
            <SectionTitle>Recent Activity</SectionTitle>
          </SectionHeader>
          {transactions.length === 0 ? (
            <div style={{ padding: '1rem 0', opacity: 0.7 }}>No transactions found.</div>
          ) : (
            <ActivityList>
              {transactions.map((tx) => (
                <ActivityItem key={tx._id || tx.txHash}>
                  <ActivityDate>{new Date(tx.createdAt).toLocaleDateString()}</ActivityDate>
                  {tx.type}: {tx.amount} {tx.tokenSymbol || tx.token}
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', opacity: 0.7 }}>
                    <TransactionHash>{shortenAddress(tx.txHash)}</TransactionHash>
                    <a 
                      href={`https://solscan.io/tx/${tx.txHash}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      <SolscanIcon />
                    </a>
                  </div>
                </ActivityItem>
              ))}
            </ActivityList>
          )}
        </ActivitySection>
      </>
    );
  };

  return (
    <DashboardLayout activeMenuItem="console">
      {isLoading && <LoadingOverlay>Loading your data...</LoadingOverlay>}
      {renderHowItWorksModal()}
      {renderDisclaimerModal()}
      {renderTermsModal()}
      
      <MainContent>
        <NavContainer>
          <NavButtonsGroup>
            <NavButton 
              onClick={() => setActiveSection('dashboard')}
              $active={activeSection === 'dashboard'}
            >
              Dashboard
            </NavButton>
            <NavButton 
              onClick={() => setActiveSection('console')}
              $active={activeSection === 'console'}
            >
              Trading Console
            </NavButton>
            <NavButton 
              onClick={() => setShowHowItWorks(true)}
              $active={false}
            >
              How it Works
            </NavButton>
          </NavButtonsGroup>
          
          <AddressGroup>
            <AddressChip 
              onClick={() => copyToClipboard(tokenAddress)}
              title="Click to copy token address"
            >
              <SolscanIcon />
              <AddressLabel onClick={(e) => {
                e.stopPropagation(); // Prevent copying address
                navigate('/holders-analytics');
              }}>Token:</AddressLabel>
              <AddressText>
                {tokenAddress.substring(0, 4)}...{tokenAddress.substring(tokenAddress.length - 4)}
              </AddressText>
            </AddressChip>
            
            <AddressChip 
              onClick={() => copyToClipboard(pairAddress)}
              title="Click to copy pair address"
            >
              <DexscreenerIcon />
              <AddressLabel>Pair:</AddressLabel>
              <AddressText>
                {pairAddress.substring(0, 4)}...{pairAddress.substring(pairAddress.length - 4)}
              </AddressText>
            </AddressChip>
          </AddressGroup>
        </NavContainer>
        
        {renderContent()}
        
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '1rem',
          gap: '1rem',
          padding: '0.5rem 0',
          fontSize: '0.8rem',
          opacity: 0.7
        }}>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setShowDisclaimer(true); }}
            style={{
              color: '#00c0ef',
              textDecoration: 'none'
            }}
          >
            Disclaimer
          </a>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
            style={{
              color: '#00c0ef',
              textDecoration: 'none'
            }}
          >
            Terms & Conditions
          </a>
        </div>
        
        {copyNotification && <CopyNotification>{copyNotification}</CopyNotification>}
      </MainContent>
    </DashboardLayout>
  );
};

export default Console; 